import React, { useState, useRef, useId, forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { Icon } from './Icon';
import { useControllable } from '../../hooks/useControllable';
import styles from './InputNumber.module.css';
import inputStyles from './Input.module.css';

type InputNumberSize = 'sm' | 'md' | 'lg';

const sizeTokens: Record<InputNumberSize, { height: string; fontSize: string; padding: string }> = {
  sm: { height: 'var(--control-height-sm)', fontSize: 'var(--text-sm)', padding: '0 var(--space-2)' },
  md: { height: 'var(--control-height-md)', fontSize: 'var(--text-base)', padding: '0 var(--space-3)' },
  lg: { height: 'var(--control-height-lg)', fontSize: 'var(--text-md)', padding: '0 var(--space-4)' },
};

const inputNumber = cva(styles.base);

interface InputNumberProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'prefix'>, VariantProps<typeof inputNumber> {
  /** Controlled value */
  value?: number | null;
  /** Default value */
  defaultValue?: number;
  /** Change handler */
  onChange?: (value: number | null) => void;
  /** Minimum */
  min?: number;
  /** Maximum */
  max?: number;
  /** Step increment */
  step?: number;
  /** Decimal precision */
  precision?: number;
  /** Disabled */
  disabled?: boolean;
  /** Prefix element */
  prefix?: React.ReactNode;
  /** Show controls */
  controls?: boolean;
  /** Size */
  size?: InputNumberSize;
  /**
   * Floating label text. Sits inside the field at rest; animates upward and
   * shrinks on focus or when the field has a non-empty value. This is the
   * ONLY label/hint API — `placeholder` is intentionally removed.
   */
  label?: string;
}

/**
 * InputNumber — numeric input with increment/decrement controls.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | value | number \| null | — | Controlled value |
 * | defaultValue | number | — | Initial value |
 * | onChange | (value) => void | — | Change handler |
 * | min | number | -Infinity | Minimum |
 * | max | number | Infinity | Maximum |
 * | step | number | 1 | Increment |
 * | precision | number | — | Decimal places |
 * | disabled | boolean | false | Disable |
 * | prefix | ReactNode | — | Prefix element |
 * | controls | boolean | true | Show up/down buttons |
 * | size | 'sm' \| 'md' \| 'lg' | 'md' | Size |
 *
 * @example
 * <InputNumber min={0} max={100} defaultValue={50} />
 * <InputNumber step={0.1} precision={2} prefix="$" />
 */
export const InputNumber = forwardRef<HTMLInputElement, InputNumberProps>(function InputNumber(
  {
    value: controlledValue,
    defaultValue,
    onChange,
    min = -Infinity,
    max = Infinity,
    step = 1,
    precision,
    disabled = false,
    prefix,
    controls = true,
    size = 'md',
    label,
    style,
    className,
    ...props
  },
  ref,
) {
  const [value, setValue] = useControllable(controlledValue, defaultValue ?? null, onChange);
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const hasValue = value !== null && value !== undefined;
  const labelFloated = focused || hasValue;
  const inputRef = useRef<HTMLInputElement>(null);
  const generatedId = useId();
  const inputId = (props as { id?: string }).id ?? generatedId;
  const sz = sizeTokens[size];

  const clamp = (n: number) => {
    let v = Math.min(max, Math.max(min, n));
    if (precision !== undefined) v = parseFloat(v.toFixed(precision));
    return v;
  };

  const increment = () => {
    if (disabled) return;
    setValue(clamp((value ?? 0) + step));
  };

  const decrement = () => {
    if (disabled) return;
    setValue(clamp((value ?? 0) - step));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '' || raw === '-') {
      setValue(null);
      return;
    }
    const n = parseFloat(raw);
    if (!isNaN(n)) setValue(n);
  };

  const handleBlur = () => {
    setFocused(false);
    if (value !== null) setValue(clamp(value));
  };

  const displayValue =
    value !== null && value !== undefined ? (precision !== undefined ? value.toFixed(precision) : String(value)) : '';

  return (
    <div
      className={clsx(
        inputNumber({ className }),
        focused && styles.focused,
        disabled && styles.disabled,
        label !== undefined && styles.withLabel,
      )}
      style={{
        '--input-number-height': sz.height,
        '--input-number-font-size': sz.fontSize,
        '--input-number-padding': sz.padding,
        ...style,
      } as React.CSSProperties}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      {...props}
    >
      {label !== undefined && (
        <label
          htmlFor={inputId}
          className={clsx(
            inputStyles.floatingLabel,
            labelFloated ? inputStyles.labelFloated : inputStyles.labelRest,
            focused && inputStyles.labelFocused,
          )}
        >
          {label}
        </label>
      )}
      {prefix && (
        <span className={styles.prefix}>
          {prefix}
        </span>
      )}
      <input
        ref={ref ?? inputRef}
        id={inputId}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            increment();
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            decrement();
          }
        }}
        disabled={disabled}
        className={styles.input}
      />
      {controls && !disabled && (
        <div className={clsx(styles.controls, (hovered || focused) && styles.controlsVisible)}>
          <motion.button
            type="button"
            tabIndex={-1}
            onClick={increment}
            whileTap={{ scale: 0.85 }}
            className={styles.controlBtn}
          >
            <Icon name="expand_less" size={12} />
          </motion.button>
          <div className={styles.controlDivider} />
          <motion.button
            type="button"
            tabIndex={-1}
            onClick={decrement}
            whileTap={{ scale: 0.85 }}
            className={styles.controlBtn}
          >
            <Icon name="expand_more" size={12} />
          </motion.button>
        </div>
      )}
    </div>
  );
});
